import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../../../lib/api';
import { toast } from '../../../stores/toast.store';

/**
 * Dipendenti — tipi e query della pagina (API: /api/v1/admin/dipendenti, vedi
 * apps/api/src/modules/admin/admin.dipendenti.ts). File a parte da adminApi.ts:
 * la pagina e' sua e non tocca le forme delle altre.
 *
 * Tutti gli importi sono PAGAMENTI OSSERVATI in banca (Qonto), non il costo
 * aziendale ufficiale, che arriva solo dal consulente del lavoro.
 */

export type EmployeeKind = 'DIPENDENTE' | 'APPRENDISTA' | 'AMMINISTRATORE';
export type PayrollKind = 'STIPENDIO' | 'RIMBORSO_KM' | 'TFR' | 'ALTRO';
export type TipoVerifica = 'TFR' | 'STIPENDIO';
export type EsitoVerifica = 'ALTROVE' | 'DA_RECUPERARE' | 'RIAPERTA';

export interface PagamentoPersona {
  id: string;
  date: string | null;
  amount: number;
  kind: PayrollKind;
  /** Mese di competenza "YYYY-MM" (per il TFR l'ultimo mese coperto). */
  month: string | null;
  causale: string;
}

export interface Persona {
  id: string;
  fullName: string;
  kind: EmployeeKind;
  role: string | null;
  partTimePct: number | null;
  hiredAt: string | null;
  endedAt: string | null;
  bankNames: string[];
  tfrRef: string | null;
  note: string | null;
  active: boolean;
  netto: { media: number | null; mesi: string[]; esclusi: string[]; insieme: boolean };
  tfrAlMese: number | null;
  ultimoStipendio: { month: string | null; paidOn: string | null; amount: number } | null;
  giornoTipico: { giorno: number; fisso: boolean; stimato: boolean };
  spesaFissa: { id: string; name: string; amount: number; nextDueDate: string | null; dayOfMonth: number } | null;
  verificheAperte: { tfr: number; stipendio: number; daRecuperare: number };
  pagamenti: PagamentoPersona[];
}

export interface Verifica {
  key: string;
  employeeId: string;
  fullName: string;
  kind: TipoVerifica;
  months: string[];
  from: string;
  to: string;
  dovevaPartire: string;
  stato: 'DA_VERIFICARE' | 'DA_RECUPERARE';
  stima: number | null;
  acconti: Array<{ month: string; amount: number }>;
  nota: string | null;
  decisoIl: string | null;
  decisoDa: string | null;
}

export interface Chiusa {
  key: string;
  employeeId: string;
  fullName: string;
  kind: TipoVerifica;
  months: string[];
  from: string;
  to: string;
  nota: string | null;
  decisoIl: string | null;
  decisoDa: string | null;
}

export interface DipendentiData {
  today: string;
  daMese: string;
  persone: Persona[];
  fascia: {
    uscitaMensile: number;
    parti: { netti: number; tfr: number; f24: number | null };
    f24Mesi: number;
    inBustaPaga: number;
    perRapporto: Record<EmployeeKind, number>;
    verifiche: number;
  };
  verifiche: Verifica[];
  chiuse: Chiusa[];
  meseTipo: {
    stipendi: Array<{ nome: string; kind: EmployeeKind; giorno: number; fisso: boolean; stimato: boolean }>;
    tfrGiorno: number | null;
    f24Giorno: number;
    jetHrGiorno: number | null;
  };
  daRecuperare: { count: number; stima: number };
}

export const dipendentiQuery = {
  queryKey: ['admin', 'dipendenti'] as const,
  queryFn: () => api.get<DipendentiData>('/admin/dipendenti'),
  staleTime: 30_000,
};

export interface EmployeeInput {
  fullName: string;
  kind: EmployeeKind;
  partTimePct: number | null;
  hiredAt: string | null;
  endedAt: string | null;
  bankNames: string[];
  tfrRef: string | null;
  note: string | null;
}

const errore = (e: unknown) => (e instanceof ApiError ? e.message : 'Riprova');

/** Salvataggi della pagina: persona (nuova, modifica, fine e ripresa del rapporto) e decisioni sui versamenti. */
export function useDipendentiActions() {
  const qc = useQueryClient();
  // Lo stipendio entra nella previsione e i bonifici cambiano stato in Banca: si rinfresca tutta l'Amministrazione.
  const refresh = () => qc.invalidateQueries({ queryKey: ['admin'] });

  const salva = useMutation({
    mutationFn: ({ id, data }: { id?: string; data: EmployeeInput }) =>
      id ? api.patch<{ id: string }>(`/admin/dipendenti/${id}`, data) : api.post<{ id: string }>('/admin/dipendenti', data),
    onSuccess: (_r, v) => {
      refresh();
      toast.ok(v.id ? 'Scheda aggiornata' : 'Persona aggiunta', v.data.fullName);
    },
    onError: (e) => toast.alert('Non salvata', errore(e)),
  });

  const termina = useMutation({
    mutationFn: ({ id, endedAt }: { id: string; endedAt: string; nome: string }) => api.post(`/admin/dipendenti/${id}/termina`, { endedAt }),
    onSuccess: (_r, v) => {
      refresh();
      toast.ok('Rapporto terminato', `${v.nome}: le spese fisse si fermano dopo l'ultima mensilità`);
    },
    onError: (e) => toast.alert('Non salvato', errore(e)),
  });

  const riprendi = useMutation({
    mutationFn: ({ id }: { id: string; nome: string }) => api.post(`/admin/dipendenti/${id}/riprendi`, {}),
    onSuccess: (_r, v) => {
      refresh();
      toast.ok('Rapporto di nuovo in corso', v.nome);
    },
    onError: (e) => toast.alert('Non salvato', errore(e)),
  });

  const decidi = useMutation({
    mutationFn: (v: { employeeId: string; kind: TipoVerifica; months: string[]; resolution: EsitoVerifica; note?: string | null }) =>
      api.post('/admin/dipendenti/verifiche', v),
    onSuccess: (_r, v) => {
      refresh();
      toast.ok(
        v.resolution === 'ALTROVE' ? 'Chiuso: pagato altrove' : v.resolution === 'DA_RECUPERARE' ? 'Segnato da recuperare' : 'Di nuovo da verificare',
        v.note ?? undefined,
      );
    },
    onError: (e) => toast.alert('Non salvato', errore(e)),
  });

  return { salva, termina, riprendi, decidi };
}
